
import {
  defineComponent,
  ref,
  watch,
  onMounted,
  onBeforeUnmount,
  PropType
} from 'vue'
import * as echarts from 'echarts'
import type { SentimentRangeItem } from '@/api/sentimentBarometer'
import {
  INDEX_OPTIONS,
  formatTradingDay,
  formatChartAxisDate,
  formatSentimentScore
} from '@/utils/sentiment/constants'
import type { IndexKey, SentimentThemeMode } from '@/utils/sentiment/constants'
import { getScoreBandColors, getSentimentTheme } from '@/utils/sentiment/theme'

/** 指数涨跌幅曲线颜色 */
const INDEX_LINE_COLORS: Record<IndexKey, string> = {
  sh: '#FE4043',
  sz: '#39E365',
  cyb: '#4FA3FF'
}

const SCORE_LINE_COLOR = '#FFAA76'
const OVERHEAT_LINE = 80
const FREEZE_LINE = 20
const THRESHOLD_LINE_HOT = 'rgba(254, 64, 67, 0.55)'
const THRESHOLD_LINE_COLD = 'rgba(77, 141, 252, 0.72)'
const THRESHOLD_LABEL_HOT = 'rgba(254, 64, 67, 0.85)'
const THRESHOLD_LABEL_COLD = 'rgba(109, 168, 255, 0.95)'
const THRESHOLD_LABEL_H = 15
/** 「过热/冰点」相对参考线的垂直偏移（px，负值上移、正值下移） */
const THRESHOLD_LABEL_HOT_OFFSET = -14
const THRESHOLD_LABEL_COLD_OFFSET = 24
/** 图表通用字号 */
const CHART_AXIS_FONT = 15
const CHART_AXIS_FONT_SM = 14
const CHART_TOOLTIP_FONT = 16
/** 进入页面默认可见的数据条数（最新 N 条） */
const DEFAULT_ZOOM_VISIBLE_COUNT = 60
/** 底部缩放条布局（日期绘在条内） */
const DATA_ZOOM_BOTTOM = 8
const DATA_ZOOM_HEIGHT = 26
const DATA_ZOOM_DATE_FONT = 12
/** 条内日期相对缩放条底边再下移（px，减小 bottom 值） */
const DATA_ZOOM_DATE_BOTTOM_EXTRA = 2
const DATA_ZOOM_DATE_INSET = 8
/** 趋势图字号与整页同比例 */
const CHART_FONT_SCALE_CAP = 1.05
/** 图表 hover 联动温度计/解读表的防抖（ms） */
const ACTIVE_INDEX_DEBOUNCE_MS = 200

const DEBUG_TREND_CHART = process.env.NODE_ENV === 'development'

/** 默认缩放至最新 visibleCount 条；不足则展示全部 */
function calcDefaultZoomRange(
  total: number,
  visibleCount = DEFAULT_ZOOM_VISIBLE_COUNT
): { start: number; end: number } {
  if (total <= 1) return { start: 0, end: 100 }
  if (total <= visibleCount) return { start: 0, end: 100 }
  const startIndex = total - visibleCount
  const start = (startIndex / (total - 1)) * 100
  return { start, end: 100 }
}

function logTrendChart(label: string, payload?: unknown) {
  if (!DEBUG_TREND_CHART) return
  if (payload !== undefined) {
    console.log(`[SentimentTrendChart] ${label}`, payload)
  } else {
    console.log(`[SentimentTrendChart] ${label}`)
  }
}

function collectPctExtent(
  list: SentimentRangeItem[],
  selectedKeys: IndexKey[]
): { min: number; max: number } | null {
  const values: number[] = []
  const fields = INDEX_OPTIONS.filter((o) => selectedKeys.includes(o.key)).map(
    (o) => o.field
  )
  list.forEach((row) => {
    fields.forEach((field) => {
      const v = row[field]
      if (v != null && !Number.isNaN(Number(v))) values.push(Number(v))
    })
  })
  if (!values.length) return null
  let min = Math.min(...values)
  let max = Math.max(...values)
  if (min === max) {
    const pad = Math.max(Math.abs(min) * 0.2, 0.5)
    min -= pad
    max += pad
  } else {
    const span = max - min
    min -= span * 0.12
    max += span * 0.12
  }
  return { min, max }
}

function nicePctAxis(val: number): string {
  const n = Number(val)
  if (Number.isNaN(n)) return ''
  const prefix = n > 0 ? '+' : ''
  return `${prefix}${n.toFixed(2)}%`
}

/** 提示框智能定位，始终保持在图表可视区域内 */
function getTooltipPosition(
  point: number[],
  _params: unknown,
  _dom: HTMLElement,
  _rect: unknown,
  size: { contentSize: number[]; viewSize: number[] }
): number[] {
  const [mouseX, mouseY] = point
  const [boxW, boxH] = size.contentSize
  const [viewW, viewH] = size.viewSize
  const gap = 16
  const padding = 8

  let x = mouseX + gap
  if (x + boxW > viewW - padding) {
    x = mouseX - boxW - gap
  }
  x = Math.max(padding, Math.min(x, viewW - boxW - padding))

  // 优先显示在光标下方，空间不足时翻到上方
  let y = mouseY + gap
  if (y + boxH > viewH - padding) {
    y = mouseY - boxH - gap
  }
  y = Math.max(padding, Math.min(y, viewH - boxH - padding))

  return [x, y]
}

export default defineComponent({
  name: 'SentimentTrendChart',
  props: {
    list: { type: Array as PropType<SentimentRangeItem[]>, default: () => [] },
    selectedIndexes: { type: Array as PropType<IndexKey[]>, default: () => [] },
    theme: { type: String as PropType<SentimentThemeMode>, default: 'dark' },
    activeIndex: { type: Number, default: -1 },
    fontScale: { type: Number, default: 1 }
  },
  emits: ['update:activeIndex'],
  setup(props, { emit }) {
    const chartRef = ref<HTMLElement | null>(null)
    let chart: echarts.ECharts | null = null
    let resizeObserver: { observe: (el: Element) => void; disconnect: () => void } | null =
      null
    let activeIndexTimer: ReturnType<typeof setTimeout> | null = null
    let thresholdGraphicTimer: ReturnType<typeof setTimeout> | null = null
    let pendingActiveIndex = -1
    let zoomRange = { start: 0, end: 100 }
    let lastListLength = 0

    const scaledFont = (size: number) =>
      Math.round(size * Math.min(props.fontScale, CHART_FONT_SCALE_CAP))

    const resetActiveIndex = () => {
      if (activeIndexTimer) {
        clearTimeout(activeIndexTimer)
        activeIndexTimer = null
      }
      pendingActiveIndex = -1
      emit('update:activeIndex', -1)
    }

    /** 十字准星联动：取 x 轴类目索引，避免误用 y 轴刻度值当 dataIndex */
    const resolvePointerDataIndex = (e: any, listLength: number): number => {
      if (!listLength) return -1
      const axesInfo = Array.isArray(e?.axesInfo) ? e.axesInfo : []
      const xInfo = axesInfo.find((a: any) => a.axisDim === 'x')
      if (xInfo != null) {
        const idx = Math.round(Number(xInfo.value))
        if (Number.isFinite(idx) && idx >= 0 && idx < listLength) return idx
      }
      if (e?.dataIndex != null) {
        const idx = Math.round(Number(e.dataIndex))
        if (Number.isFinite(idx) && idx >= 0 && idx < listLength) return idx
      }
      return -1
    }

    const scheduleActiveIndex = (idx: number) => {
      if (typeof idx !== 'number' || idx < 0) return
      pendingActiveIndex = idx
      if (activeIndexTimer) clearTimeout(activeIndexTimer)
      activeIndexTimer = setTimeout(() => {
        activeIndexTimer = null
        emit('update:activeIndex', pendingActiveIndex)
      }, ACTIVE_INDEX_DEBOUNCE_MS)
    }

    const buildOption = () => {
      const tokens = getSentimentTheme(props.theme)
      const bands = getScoreBandColors(props.theme)
      const list = props.list || []
      const total = list.length
      const scoreData = list.map((item) => item.TotalScore ?? null)
      const showZoom = list.length > DEFAULT_ZOOM_VISIBLE_COUNT
      const pctExtent = collectPctExtent(list, props.selectedIndexes)

      const indexSeries = INDEX_OPTIONS.filter((opt) =>
        props.selectedIndexes.includes(opt.key)
      ).map((opt) => ({
        name: opt.label,
        type: 'line' as const,
        yAxisIndex: 1,
        smooth: true,
        symbol: 'none',
        lineStyle: { width: 1.5, color: INDEX_LINE_COLORS[opt.key] },
        itemStyle: { color: INDEX_LINE_COLORS[opt.key] },
        data: list.map((row) => {
          const v = row[opt.field]
          return v != null ? Number(v) : null
        })
      }))

      logTrendChart('buildOption', {
        selectedIndexes: [...props.selectedIndexes],
        listLength: list.length,
        indexSeriesCount: indexSeries.length,
        indexSeriesDetail: indexSeries.map((s, i) => {
          const opt = INDEX_OPTIONS.find((o) => o.label === s.name)
          const data = s.data as (number | null)[]
          const valid = data.filter((v) => v != null && !Number.isNaN(v))
          return {
            name: s.name,
            key: opt?.key,
            field: opt?.field,
            color: opt ? INDEX_LINE_COLORS[opt.key] : undefined,
            validPointCount: valid.length,
            sample: valid.slice(0, 3)
          }
        }),
        scoreSeries: {
          name: '情绪值',
          color: SCORE_LINE_COLOR,
          note: '情绪值曲线始终绘制，与上方指数勾选无关',
          validPointCount: scoreData.filter((v) => v != null && !Number.isNaN(v)).length,
          sample: scoreData.filter((v) => v != null).slice(0, 3)
        },
        totalSeriesOnChart: 1 + indexSeries.length,
        seriesNames: ['情绪值', ...indexSeries.map((s) => s.name)],
        pctExtent
      })

      const markArea = {
        silent: true,
        data: [
          [{ yAxis: 80, itemStyle: { color: bands[0] } }, { yAxis: 100 }],
          [{ yAxis: 60, itemStyle: { color: bands[1] } }, { yAxis: 80 }],
          [{ yAxis: 40, itemStyle: { color: bands[2] } }, { yAxis: 60 }],
          [{ yAxis: 20, itemStyle: { color: bands[3] } }, { yAxis: 40 }],
          [{ yAxis: 0, itemStyle: { color: bands[4], borderWidth: 0 } }, { yAxis: 20 }]
        ]
      }

      const markLineLabelHidden = { show: false }

      const scoreSeries = {
        name: '情绪值',
        type: 'line',
        yAxisIndex: 0,
        smooth: true,
        symbol: 'circle',
        symbolSize: 4,
        showSymbol: false,
        lineStyle: { width: 2, color: SCORE_LINE_COLOR },
        itemStyle: {
          color: SCORE_LINE_COLOR,
          borderColor: SCORE_LINE_COLOR,
          borderWidth: 1
        },
        data: scoreData,
        markArea,
        markLine: {
          silent: true,
          symbol: 'none',
          label: markLineLabelHidden,
          lineStyle: { type: 'dashed', width: 1 },
          data: [
            {
              yAxis: OVERHEAT_LINE,
              lineStyle: { color: THRESHOLD_LINE_HOT, type: 'dashed', width: 1 },
              label: markLineLabelHidden
            },
            {
              yAxis: FREEZE_LINE,
              lineStyle: { color: THRESHOLD_LINE_COLD, type: 'dashed', width: 1.5 },
              label: markLineLabelHidden
            }
          ]
        }
      }

      const series: any[] = [scoreSeries, ...indexSeries]

      const hasIndexAxis = Boolean(pctExtent && indexSeries.length)
      const axisPointerColor =
        props.theme === 'dark' ? 'rgba(255, 255, 255, 0.4)' : 'rgba(0, 0, 0, 0.28)'
      const axisPointerLabelBg = props.theme === 'dark' ? '#3d3d3d' : '#ffffff'
      const axisPointerLabelColor = props.theme === 'dark' ? '#e4e4e4' : '#333333'
      const axisPointerLabelBorder = props.theme === 'dark' ? '#555555' : '#dddddd'

      const yAxis: any[] = [
        {
          type: 'value',
          position: 'left',
          min: 0,
          max: 100,
          interval: 20,
          splitLine: { lineStyle: { color: tokens.chartGrid, type: 'dashed' } },
          axisLabel: {
            color: tokens.chartAxis,
            fontSize: scaledFont(CHART_AXIS_FONT),
            margin: 6
          }
        }
      ]

      if (pctExtent && indexSeries.length) {
        yAxis.push({
          type: 'value',
          position: 'right',
          min: pctExtent.min,
          max: pctExtent.max,
          splitLine: { show: false },
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: { show: false },
          axisPointer: {
            show: true,
            label: {
              show: true,
              backgroundColor: axisPointerLabelBg,
              color: axisPointerLabelColor,
              borderColor: axisPointerLabelBorder,
              borderWidth: 1,
              padding: [3, 5],
              fontSize: scaledFont(CHART_AXIS_FONT_SM),
              formatter: (params: any) => nicePctAxis(Number(params.value))
            }
          }
        })
      }

      /** 右侧预留「过热/冰点」；有指数时略加宽以容纳十字准星刻度 */
      const gridRight = hasIndexAxis ? 52 : 40

      return {
        backgroundColor: 'transparent',
        grid: {
          left: 52,
          right: gridRight,
          top: 12,
          bottom: showZoom ? DATA_ZOOM_BOTTOM + DATA_ZOOM_HEIGHT + 10 : 22,
          containLabel: false
        },
        tooltip: {
          trigger: 'axis',
          renderMode: 'html',
          appendToBody: true,
          confine: true,
          className: 'sentiment-trend-chart-tooltip',
          position: getTooltipPosition,
          axisPointer: {
            type: 'cross',
            snap: true,
            yAxisIndex: hasIndexAxis ? [0, 1] : 0,
            crossStyle: {
              color: axisPointerColor,
              width: 1,
              type: 'dashed'
            },
            lineStyle: {
              color: axisPointerColor,
              width: 1,
              type: 'dashed'
            },
            label: {
              show: true,
              backgroundColor: axisPointerLabelBg,
              color: axisPointerLabelColor,
              borderColor: axisPointerLabelBorder,
              borderWidth: 1,
              padding: [3, 5],
              fontSize: scaledFont(CHART_AXIS_FONT_SM),
              formatter: (params: any) => {
                if (params.axisDimension === 'y') {
                  if (params.axisIndex === 0) {
                    return formatSentimentScore(params.value)
                  }
                  if (params.axisIndex === 1 && hasIndexAxis) {
                    return nicePctAxis(Number(params.value))
                  }
                }
                if (params.axisDimension === 'x') {
                  const i = Number(params.value)
                  if (Number.isFinite(i) && i >= 0 && i < list.length) {
                    return formatChartAxisDate(list[i].tradingDay)
                  }
                }
                return params.value
              }
            }
          },
          backgroundColor: '#ffffff',
          borderColor: '#e8e8e8',
          borderWidth: 1,
          padding: [10, 12],
          textStyle: { color: '#333333', fontSize: scaledFont(CHART_TOOLTIP_FONT) },
          extraCssText: 'box-shadow: 0 2px 8px rgba(0,0,0,0.12); pointer-events: none;',
          formatter(params: any[]) {
            if (!params?.length) return ''
            const idx = params[0].dataIndex
            const row = list[idx]
            if (!row) return ''
            const date = formatTradingDay(row.tradingDay)
            const labelColor = '#333333'
            const seriesDot = (color: string) =>
              `<span style="display:inline-block;width:6px;height:6px;border-radius:50%;background:${color};margin-right:6px;vertical-align:middle;"></span>`
            let html = `<div style="margin-bottom:6px;color:#666">${date}</div>`
            params.forEach((p) => {
              const val = p.value
              const lineColor = p.color || SCORE_LINE_COLOR
              let display: string
              let valueColor = labelColor
              if (p.seriesName === '情绪值') {
                display = formatSentimentScore(val)
              } else {
                display =
                  val != null && !Number.isNaN(Number(val))
                    ? nicePctAxis(Number(val))
                    : '--'
                const numVal = Number(val)
                valueColor =
                  numVal > 0 ? '#FE4043' : numVal < 0 ? '#39E365' : '#666666'
              }
              html += `<div style="line-height:1.6">${seriesDot(lineColor)}<span style="color:${labelColor}">${p.seriesName}：</span><span style="color:${valueColor}">${display}</span></div>`
            })
            return html
          }
        },
        dataZoom: showZoom
          ? [
              {
                type: 'slider',
                show: true,
                xAxisIndex: 0,
                height: DATA_ZOOM_HEIGHT,
                bottom: DATA_ZOOM_BOTTOM,
                start: zoomRange.start,
                end: zoomRange.end,
                showDetail: false,
                labelFormatter: () => '',
                borderColor: 'transparent',
                backgroundColor:
                  props.theme === 'dark' ? 'rgba(255,255,255,0.16)' : 'rgba(0,0,0,0.04)',
                fillerColor:
                  props.theme === 'dark' ? 'rgba(255,255,255,0.28)' : 'rgba(0,0,0,0.08)',
                handleSize: 12,
                handleStyle: {
                  color: props.theme === 'dark' ? '#666666' : '#bbbbbb',
                  borderColor: props.theme === 'dark' ? '#888888' : '#999999'
                },
                textStyle: { color: 'transparent', fontSize: 1 }
              }
            ]
          : [],
        xAxis: {
          type: 'category',
          data: list.map((_, i) => i),
          boundaryGap: false,
          axisLine: { lineStyle: { color: tokens.chartGrid } },
          axisLabel: { show: false },
          axisTick: { show: false }
        },
        yAxis,
        series
      }
    }

    const updateChartGraphics = () => {
      if (!chart || !props.list?.length) return
      const list = props.list
      const total = list.length
      const tokens = getSentimentTheme(props.theme)
      const sliderDateFont = scaledFont(DATA_ZOOM_DATE_FONT)
      const sliderDateColor =
        props.theme === 'dark' ? 'rgba(255, 255, 255, 0.72)' : 'rgba(0, 0, 0, 0.55)'
      const showZoom = total > DEFAULT_ZOOM_VISIBLE_COUNT
      const hasIndexAxis = Boolean(
        collectPctExtent(list, props.selectedIndexes) &&
          props.selectedIndexes.length
      )
      const gridLeft = 52
      const gridRight = hasIndexAxis ? 52 : 40
      const graphics: any[] = []
      const mid = Math.max(0, Math.floor((total - 1) / 2))
      const half = THRESHOLD_LABEL_H / 2
      const thresholdLabelRight = 6

      try {
        const ptHot = chart.convertToPixel({ xAxisIndex: 0, yAxisIndex: 0 }, [
          mid,
          OVERHEAT_LINE
        ]) as number[]
        const ptCold = chart.convertToPixel({ xAxisIndex: 0, yAxisIndex: 0 }, [
          mid,
          FREEZE_LINE
        ]) as number[]
        if (ptHot?.length) {
          graphics.push({
            id: 'threshold-hot',
            type: 'text',
            right: thresholdLabelRight,
            top: ptHot[1] - half + THRESHOLD_LABEL_HOT_OFFSET,
            style: {
              text: '过热',
              fill: THRESHOLD_LABEL_HOT,
              fontSize: THRESHOLD_LABEL_H,
              fontWeight: 500,
              textAlign: 'right'
            }
          })
        }
        if (ptCold?.length) {
          graphics.push({
            id: 'threshold-cold',
            type: 'text',
            right: thresholdLabelRight,
            top: ptCold[1] - half + THRESHOLD_LABEL_COLD_OFFSET,
            style: {
              text: '冰点',
              fill: THRESHOLD_LABEL_COLD,
              fontSize: THRESHOLD_LABEL_H,
              fontWeight: 500,
              textAlign: 'right'
            }
          })
        }

        if (showZoom) {
          const datasetStartText = formatChartAxisDate(list[0]?.tradingDay)
          const datasetEndText = formatChartAxisDate(list[total - 1]?.tradingDay)
          if (datasetStartText) {
            graphics.push({
              id: 'datazoom-date-start',
              type: 'text',
              left: gridLeft + DATA_ZOOM_DATE_INSET,
              bottom: DATA_ZOOM_BOTTOM - DATA_ZOOM_DATE_BOTTOM_EXTRA,
              z: 100,
              style: {
                text: datasetStartText,
                fill: sliderDateColor,
                fontSize: sliderDateFont,
                fontWeight: 400,
                textAlign: 'left',
                textVerticalAlign: 'bottom'
              }
            })
          }
          if (datasetEndText) {
            graphics.push({
              id: 'datazoom-date-end',
              type: 'text',
              right: gridRight + DATA_ZOOM_DATE_INSET,
              bottom: DATA_ZOOM_BOTTOM - DATA_ZOOM_DATE_BOTTOM_EXTRA,
              z: 100,
              style: {
                text: datasetEndText,
                fill: sliderDateColor,
                fontSize: sliderDateFont,
                fontWeight: 400,
                textAlign: 'right',
                textVerticalAlign: 'bottom'
              }
            })
          }
        }
      } catch {
        return
      }

      chart.setOption({ graphic: graphics }, false)
    }

    /** 避免在 ECharts 主渲染流程中嵌套 setOption */
    const scheduleUpdateChartGraphics = () => {
      if (thresholdGraphicTimer) clearTimeout(thresholdGraphicTimer)
      thresholdGraphicTimer = setTimeout(() => {
        thresholdGraphicTimer = null
        updateChartGraphics()
      }, 0)
    }

    const onChartFinished = () => {
      scheduleUpdateChartGraphics()
    }

    const onDataZoom = (e: any) => {
      const payload = Array.isArray(e?.batch) ? e.batch[0] : e
      if (payload?.start == null || payload?.end == null) return
      zoomRange = { start: payload.start, end: payload.end }
      scheduleUpdateChartGraphics()
    }

    const render = () => {
      if (!chartRef.value) return
      if (!chart) {
        chart = echarts.init(chartRef.value, undefined, {
          devicePixelRatio: Math.min(window.devicePixelRatio || 1, 2)
        })
      }
      const total = props.list?.length ?? 0
      if (total !== lastListLength) {
        zoomRange = calcDefaultZoomRange(total)
        lastListLength = total
      }
      const option = buildOption()
      logTrendChart('render setOption', {
        selectedIndexes: [...props.selectedIndexes],
        echartsSeriesCount: option.series?.length,
        echartsSeriesNames: (option.series as { name?: string }[] | undefined)?.map(
          (s) => s.name
        )
      })
      chart.setOption(option, true)
      chart.off('finished', onChartFinished)
      chart.on('finished', onChartFinished)
      chart.off('dataZoom', onDataZoom)
      chart.on('dataZoom', onDataZoom)
      scheduleUpdateChartGraphics()
      chart.off('updateAxisPointer')
      chart.on('updateAxisPointer', (e: any) => {
        const idx = resolvePointerDataIndex(e, props.list?.length ?? 0)
        if (idx >= 0) scheduleActiveIndex(idx)
      })
      const zr = chart.getZr()
      zr.off('globalout', resetActiveIndex)
      zr.on('globalout', resetActiveIndex)
    }

    const resize = () => {
      chart?.resize()
      scheduleUpdateChartGraphics()
    }

    watch(
      () => [props.list, props.selectedIndexes, props.theme, props.fontScale],
      () => render(),
      { deep: true }
    )

    onMounted(() => {
      render()
      window.addEventListener('resize', resize)
      if (chartRef.value) {
        const ResizeObserverCtor = (window as any).ResizeObserver
        if (typeof ResizeObserverCtor === 'function') {
          const observer = new ResizeObserverCtor(() => resize())
          observer.observe(chartRef.value)
          resizeObserver = observer
        }
      }
    })

    onBeforeUnmount(() => {
      window.removeEventListener('resize', resize)
      resizeObserver?.disconnect()
      resizeObserver = null
      if (activeIndexTimer) clearTimeout(activeIndexTimer)
      activeIndexTimer = null
      if (thresholdGraphicTimer) clearTimeout(thresholdGraphicTimer)
      thresholdGraphicTimer = null
      chart?.off('finished', onChartFinished)
      chart?.off('dataZoom', onDataZoom)
      chart?.getZr()?.off('globalout', resetActiveIndex)
      chart?.dispose()
      chart = null
    })

    return { chartRef }
  }
})
